import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useI18n } from '../../contexts/I18nContext';
import { userService } from '../../services/userService';
import { clientService } from '../../services/clientService';
import { UserWithDetails, UserGroup } from '../../types/user';
import { UserDetailModal } from './UserDetailModal';
import config from '../../config/env';

// Role hierarchy for filtering (higher index = higher privilege)
const ROLE_HIERARCHY = ['Viewer', 'ClientAdmin', 'Admin', 'SuperAdmin'];

// Backward compat: treat Operator as ClientAdmin
const normalizeRoleName = (role: string): string => role === 'Operator' ? 'ClientAdmin' : role;

interface UserWithGroupsAndRoles extends UserWithDetails {
  groups?: UserGroup[];
  clientRoles?: string[];
}

interface UsersTableProps {
  onUserSelect?: (user: UserWithDetails) => void;
  refreshTrigger?: number;
}

export const UsersTable: React.FC<UsersTableProps> = ({ onUserSelect, refreshTrigger }) => {
  const { isAuthenticated, user: currentUser } = useAuth();
  const { isAdmin } = useClientAccess();
  // Soft-delete: admins can opt in to see Keycloak-disabled users. Default off.
  const [showInactive, setShowInactive] = useState(false);
  const [users, setUsers] = useState<UserWithGroupsAndRoles[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof UserWithDetails>('username');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<UserWithDetails | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [clientNames, setClientNames] = useState<Map<string, string>>(new Map());

  // Get the highest role level of the current user
  const currentUserRoleLevel = useMemo(() => {
    if (!currentUser?.roles) return -1;
    let maxLevel = -1;
    for (const role of currentUser.roles) {
      const level = ROLE_HIERARCHY.indexOf(normalizeRoleName(role));
      if (level > maxLevel) maxLevel = level;
    }
    return maxLevel;
  }, [currentUser?.roles]);

  // Check if current user is SuperAdmin
  const isSuperAdmin = useMemo(() => {
    return currentUser?.roles?.includes('SuperAdmin') || false;
  }, [currentUser?.roles]);

  // Resolve client names from user groups
  const resolveClientNames = async (usersList: UserWithGroupsAndRoles[]) => {
    const clientUuids = new Set<string>();
    usersList.forEach(user => {
      (user.groups || []).forEach(group => {
        const match = group.path.match(/\/Clients\/([a-f0-9-]+)/i);
        if (match) {
          clientUuids.add(match[1]);
        }
      });
    });

    if (clientUuids.size > 0) {
      const nameMap = new Map<string, string>();
      for (const uuid of clientUuids) {
        const name = await clientService.getClientName(uuid);
        nameMap.set(uuid, name);
      }
      setClientNames(nameMap);
    }
  };

  // Get client names for a user
  const getUserClientNames = (user: UserWithGroupsAndRoles): string => {
    const clientIds = (user.groups || [])
      .filter(g => g.path.startsWith('/Clients/'))
      .map(g => g.path.replace('/Clients/', ''));

    if (clientIds.length === 0) return '-';

    const names = clientIds.map(id => clientNames.get(id) || id);
    return names.join(', ');
  };

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isAuthenticated || !currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const canSeeInactive = isAdmin || isSuperAdmin;
        const response = await userService.fetchUsers(
          0, 500, searchTerm || undefined, showInactive && canSeeInactive,
        );

        const usersWithDetails: UserWithGroupsAndRoles[] = response.users.map(user =>
          userService.transformToUserWithDetails(user)
        );

        // For non-SuperAdmin users, fetch groups and roles for filtering
        if (!isSuperAdmin) {
          setLoadingDetails(true);

          // Fetch groups and roles for each user in parallel
          const usersWithGroupsAndRoles = await Promise.all(
            usersWithDetails.map(async (user) => {
              try {
                const [groups, rolesResponse] = await Promise.all([
                  userService.getUserGroups(user.id).catch(() => []),
                  userService.getUserClientRoles(user.id, config.oauth.clientId).catch(() => ({ roles: [] }))
                ]);
                return {
                  ...user,
                  groups,
                  clientRoles: rolesResponse.roles?.map((r: any) => r.name) || []
                };
              } catch {
                return { ...user, groups: [], clientRoles: [] };
              }
            })
          );

          // Filter users based on:
          // 1. Client group membership (must share at least one client group)
          // 2. Role hierarchy (can only see users with same or lower role level)
          const filteredUsers = usersWithGroupsAndRoles.filter(user => {
            // Always show the current user themselves
            if (user.id === currentUser.id) return true;

            // Check client group membership
            const userClientIds = (user.groups || [])
              .filter(g => g.path.startsWith('/Clients/'))
              .map(g => g.path.replace('/Clients/', ''));

            const currentUserClientIds = currentUser.clientIds || [];
            const hasMatchingClient = userClientIds.some(id => currentUserClientIds.includes(id));

            if (!hasMatchingClient && currentUserClientIds.length > 0) {
              return false;
            }

            // Check role hierarchy
            const userRoles = user.clientRoles || [];
            let userMaxRoleLevel = -1;
            for (const role of userRoles) {
              const level = ROLE_HIERARCHY.indexOf(normalizeRoleName(role));
              if (level > userMaxRoleLevel) userMaxRoleLevel = level;
            }

            // If user has no roles, they have the lowest level (can be seen by anyone)
            // If current user's role level >= user's role level, they can see them
            return currentUserRoleLevel >= userMaxRoleLevel;
          });

          setUsers(filteredUsers);

          // Resolve client names for display
          await resolveClientNames(filteredUsers);
          setLoadingDetails(false);
        } else {
          // SuperAdmin can see all users - also need to fetch groups for them
          setLoadingDetails(true);
          const usersWithGroups = await Promise.all(
            usersWithDetails.map(async (user) => {
              try {
                const groups = await userService.getUserGroups(user.id).catch(() => []);
                return { ...user, groups };
              } catch {
                return { ...user, groups: [] };
              }
            })
          );
          setUsers(usersWithGroups);
          await resolveClientNames(usersWithGroups);
          setLoadingDetails(false);
        }
      } catch (err: any) {
        console.error('[USERS TABLE] Error fetching users:', err);

        let errorMessage = 'Failed to fetch users';
        if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isAuthenticated, refreshTrigger, searchTerm, currentUser, isSuperAdmin, currentUserRoleLevel, showInactive, isAdmin]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalUsers = users.length;
    const enabledUsers = users.filter(u => u.enabled).length;
    const disabledUsers = users.filter(u => !u.enabled).length;
    const verifiedUsers = users.filter(u => u.emailVerified).length;
    return { totalUsers, enabledUsers, disabledUsers, verifiedUsers };
  }, [users]);

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users.filter((user) => {
      if (!user?.id || !user?.username) {
        return false;
      }

      const searchLower = searchTerm.toLowerCase();
      return (
        user.username.toLowerCase().includes(searchLower) ||
        (user.email?.toLowerCase().includes(searchLower) || false) ||
        (user.firstName?.toLowerCase().includes(searchLower) || false) ||
        (user.lastName?.toLowerCase().includes(searchLower) || false) ||
        user.fullName.toLowerCase().includes(searchLower)
      );
    });

    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      }

      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [users, searchTerm, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedUsers, currentPage, itemsPerPage]);

  const handleSort = (field: keyof UserWithDetails) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleRowClick = (user: UserWithDetails) => {
    setSelectedUser(user);
    setIsModalOpen(true);
    if (onUserSelect) {
      onUserSelect(user);
    }
  };

  const getSortIcon = (field: keyof UserWithDetails) => {
    if (field !== sortField) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const { formatDate: i18nFormatDate, t } = useI18n();
  const formatDate = (date?: Date): string => {
    if (!date) return 'N/A';
    return i18nFormatDate(date);
  };

  if (loading || loadingDetails) {
    return (
      <div className="users-loading">
        <div className="loading-spinner"></div>
        <p>{loadingDetails ? t('users.loadingDetails') : t('users.loadingList')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="users-error">
        <span className="error-icon">!</span>
        <h3>{t('users.errorLoading')}</h3>
        <p>{error}</p>
        <button className="retry-button" onClick={() => window.location.reload()}>{t('errors.retryAction')}</button>
      </div>
    );
  }

  return (
    <div className="users-container">
      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalUsers}</div>
            <div className="stat-label">{t('users.totalUsers')}</div>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">✓</div>
          <div className="stat-content">
            <div className="stat-value">{stats.enabledUsers}</div>
            <div className="stat-label">{t('users.enabled')}</div>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">⊘</div>
          <div className="stat-content">
            <div className="stat-value">{stats.disabledUsers}</div>
            <div className="stat-label">{t('users.disabled')}</div>
          </div>
        </div>

        <div className="stat-card secondary">
          <div className="stat-icon">✉</div>
          <div className="stat-content">
            <div className="stat-value">{stats.verifiedUsers}</div>
            <div className="stat-label">{t('users.verified')}</div>
          </div>
        </div>
      </div>

      {/* Main Table Section */}
      <div className="users-section">
        <div className="section-header">
          <div className="section-title">
            <h2>{t('users.allUsers')}</h2>
          </div>
          <div className="section-controls">
            <div className="search-container">
              <input
                type="text"
                data-testid="field-users-search"
                placeholder={t('users.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />
            </div>
            {(isAdmin || isSuperAdmin) && (
              <label className="filter-toggle-inactive" title={t('common.showInactiveHint')}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    setCurrentPage(1);
                  }}
                />
                {t('common.showInactive')}
              </label>
            )}
          </div>
        </div>

        <div className="table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('username')} className="sortable">
                  {t('users.username')} {getSortIcon('username')}
                </th>
                <th onClick={() => handleSort('fullName')} className="sortable">
                  {t('users.fullName')} {getSortIcon('fullName')}
                </th>
                <th onClick={() => handleSort('email')} className="sortable">
                  {t('users.email')} {getSortIcon('email')}
                </th>
                <th onClick={() => handleSort('createdDate')} className="sortable">
                  {t('users.created')} {getSortIcon('createdDate')}
                </th>
                <th>{t('clients.clients')}</th>
                <th>{t('clients.status')}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user) => (
                <tr
                  key={user.id}
                  onClick={() => handleRowClick(user)}
                  className="user-row"
                >
                  <td className="user-username">{user.username}</td>
                  <td className="user-fullname">{user.fullName}</td>
                  <td className="user-email">{user.email || 'N/A'}</td>
                  <td className="user-created">{formatDate(user.createdDate)}</td>
                  <td className="user-clients">{getUserClientNames(user)}</td>
                  <td className="user-status">
                    <span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>
                      {user.enabled ? t('users.enabledBadge') : t('users.disabledBadge')}
                    </span>
                    {user.emailVerified && (
                      <span className="verified-badge" title={t('users.emailVerified')}>
                        ✓
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {paginatedUsers.length === 0 && !loading && (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <h3>{t('users.noUsersFound')}</h3>
            </div>
          )}
        </div>

        <div className="pagination-wrapper">
          <div className="pagination-info">
            <span>
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedUsers.length)} of {filteredAndSortedUsers.length} users
            </span>
            <div className="items-per-page">
              <label htmlFor="usersItemsPerPage">Items per page:</label>
              <select
                id="usersItemsPerPage"
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="items-per-page-select"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                First
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Previous
              </button>

              <div className="page-numbers">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page: number;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`page-btn ${page === currentPage ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Last
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Activity Footer */}
      <div className="users-footer">
        <div className="activity-summary">
          <div className="activity-item">
            <span className="activity-icon">👥</span>
            <span className="activity-text">
              <strong>{stats.totalUsers}</strong> total users
            </span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">✓</span>
            <span className="activity-text">
              <strong>{stats.enabledUsers}</strong> enabled
            </span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">✉</span>
            <span className="activity-text">
              <strong>{Math.round((stats.verifiedUsers / stats.totalUsers) * 100) || 0}%</strong> email verified
            </span>
          </div>
        </div>
      </div>

      <UserDetailModal
        user={selectedUser}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
        }}
        onUserUpdated={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
          window.location.reload();
        }}
      />
    </div>
  );
};
